/**
 * Grace Pointe Child Development Center — Site Content
 * -------------------------------------------------------
 * All user-facing text lives here so a non-technical owner
 * can update copy without touching component code.
 *
 * Fields marked TODO should be confirmed with the daycare owner.
 */

export const SITE = {
  name:      'Grace Pointe Child Development Center',
  shortName: 'Grace Pointe CDC',
  tagline:   'Nurturing Hearts, Minds & Souls',
  description:
    'A Christ-centered, multicultural child development center in Avon, Indiana offering superior childcare for infants, toddlers, and preschoolers.',

  contact: {
    phone:   '(317) 837-3307',
    email:   'gpcdm@indygracepointe.org',
    address: {
      street:  '10951 E CR 100 S.',
      city:    'Indianapolis',
      state:   'IN',
      zip:     '46231',
      display: '10951 E CR 100 S., Indianapolis, IN 46231',
      // Note: physically near Avon, IN (western Indianapolis suburb)
    },
  },

  hours: {
    days:  'Monday – Friday',
    open:  '7:00 AM',
    close: '5:30 PM',
    display: 'Monday – Friday  |  7:00 AM – 5:30 PM',
  },

  social: {
    // TODO: Add social media links if/when available
    facebook:  '',
    instagram: '',
  },

  procare: {
    url: 'https://schools.procareconnect.com/login',
    label: 'Parent Portal (Procare)',
  },
} as const

// ─── Hero section ─────────────────────────────────────────────────────────────
export const HERO = {
  headline:    'Where Every Child is Known,\nLoved, and Inspired',
  subheadline:
    'Grace Pointe Child Development Center provides superior, Christ-centered care\nfor infants, toddlers, and preschoolers in the Avon, Indiana area.',
  cta1: { label: 'Schedule a Tour',  href: '#contact' },
  cta2: { label: 'View Programs',    href: '#programs' },
  trustLine:
    'Proudly serving families in Avon, Plainfield, and the western Indianapolis community.',
}

// ─── Statement / Intro ────────────────────────────────────────────────────────
export const STATEMENT = {
  headline: 'A Sacred Trust',
  body:
    'We know your children are your most treasured possession. We consider caring for and assisting you in the development of your children as a sacred trust. We diligently serve the families of our surrounding communities by offering superior childcare and development in a multicultural, Christian environment.',
}

// ─── Mission ──────────────────────────────────────────────────────────────────
export const MISSION = {
  headline: 'Our Mission & Vision',
  quote:
    '"To nurture the heart, soul, and mind of every child in Christ-centered love and care — supporting families and staff in spiritual growth and excellence."',
  // TODO: Add a longer mission/vision narrative if available from owner
  pillars: [
    {
      icon: 'heart',
      title: 'Nurturing Hearts',
      body:  'Every child is welcomed with warmth, compassion, and the grace they deserve from their earliest days.',
    },
    {
      icon: 'book-open',
      title: 'Growing Minds',
      body:  'Age-appropriate, play-based learning experiences that build curiosity, creativity, and foundational skills.',
    },
    {
      icon: 'users',
      title: 'Strengthening Families',
      body:  'We partner with parents to support each child\'s unique journey, keeping communication open and caring.',
    },
    {
      icon: 'globe',
      title: 'Multicultural Community',
      body:  'We celebrate diversity and create an inclusive environment that reflects the beautiful variety of our community.',
    },
  ],
}

// ─── Programs ─────────────────────────────────────────────────────────────────
export const PROGRAMS = {
  headline:    'Our Programs',
  subheadline: 'Developmentally appropriate care and education for every stage.',
  items: [
    {
      id:       'infant',
      title:    'Infant Care',
      ageRange: '6 weeks – 12 months',
      // TODO: Confirm exact age ranges and any specific ratio or curriculum details with owner
      description:
        'Our nurturing infant program provides a safe, loving environment where the youngest learners receive individualized attention, sensory exploration, and consistent routines that help them feel secure and thrive.',
      features: ['Low caregiver-to-infant ratios', 'Consistent daily routines', 'Sensory play and exploration', 'Regular parent communication'],
      icon: '🌱',
      available: true,
    },
    {
      id:       'toddler',
      title:    'Toddler Program',
      ageRange: '12 months – 3 years',
      description:
        'Toddlers are natural explorers! Our toddler classrooms are designed to encourage language development, independence, social-emotional growth, and hands-on discovery in a warm, structured setting.',
      features: ['Language-rich environment', 'Motor skill development', 'Social-emotional learning', 'Creative arts and music'],
      icon: '🌼',
      available: true,
    },
    {
      id:       'preschool',
      title:    'Preschool (3-Year-Olds)',
      ageRange: '3 years',
      description:
        'Our three-year-old preschool class builds the foundational academic and social skills children need to be confident, curious learners — preparing hearts and minds for kindergarten and beyond.',
      features: ['Pre-literacy and numeracy', 'Christian values integration', 'STEM exploration', 'Circle time and group activities'],
      icon: '⭐',
      available: true,
    },
    {
      id:       'prek',
      // TODO: Confirm whether a Pre-K / 4-year-old class is offered
      title:    'Pre-K Program',
      ageRange: '4–5 years',
      description:
        'TODO: Confirm with owner whether a Pre-K class for 4-year-olds is currently offered. If so, add program details here.',
      features: ['Kindergarten readiness', 'Early literacy', 'Problem-solving skills', 'Character development'],
      icon: '🎓',
      available: false, // Set to true if confirmed
    },
  ],
  enrollmentNote:
    'We currently have openings in our Infant, Toddler, and 3-year-old classes! Contact us to schedule a personal tour.',
}

// ─── Why Choose Us / Features ─────────────────────────────────────────────────
export const FEATURES = [
  {
    icon: 'shield-check',
    title: 'Safe & Secure',
    body:  'Secure facility with attentive staff ensuring every child\'s safety throughout the day.',
  },
  {
    icon: 'heart-handshake',
    title: 'Caring Teachers',
    body:  'Passionate educators who treat every child as an individual and love what they do.',
  },
  {
    icon: 'play-circle',
    title: 'Learning Through Play',
    body:  'Research-based, play-driven curriculum that makes learning joyful and effective.',
  },
  {
    icon: 'map-pin',
    title: 'Avon Area Location',
    body:  'Conveniently located near Avon, Plainfield, and western Indianapolis — close to home and work.',
  },
  {
    icon: 'book-open',
    title: 'Christian Foundation',
    body:  'Christ-centered values woven naturally into daily life, building character alongside curiosity.',
  },
  {
    icon: 'globe-2',
    title: 'Multicultural Environment',
    body:  'An inclusive community that celebrates diversity and teaches children to love and respect others.',
  },
]

// ─── Enrollment ───────────────────────────────────────────────────────────────
export const ENROLLMENT = {
  headline:    'Join Our Grace Pointe Family',
  subheadline: 'We\'re so glad you\'re considering Grace Pointe for your child\'s early learning journey.',
  steps: [
    {
      step: '1',
      title: 'Schedule a Tour',
      body:  'Call or email us to arrange a personal tour. Meet our staff, see our classrooms, and ask all your questions.',
    },
    {
      step: '2',
      title: 'Complete an Application',
      body:  'Download and complete the Enrollment Packet for the 2025–2026 school year.',
    },
    {
      step: '3',
      title: 'Review Fees & Calendar',
      body:  'Review the Schedule of Fees and School Calendar for 2025–2026 to plan your child\'s start.',
    },
    {
      step: '4',
      title: 'Welcome to Grace Pointe!',
      body:  'Once enrolled, we\'ll guide you and your child through a smooth, warm transition into our program.',
    },
  ],
  documents: [
    { label: 'Enrollment Packet 2025–2026',  href: '/pdfs/Enrollment-Packet-2025.pdf' },
    { label: 'Schedule of Fees 2025–2026',   href: '/pdfs/Fee-Schedule-2025-2026.pdf' },
    { label: 'School Calendar 2025–2026',    href: '/pdfs/Calendar-2025-2026.pdf' },
  ],
  cta: {
    label: 'Contact Us to Schedule a Tour',
    href:  '#contact',
  },
}

// ─── Gallery ──────────────────────────────────────────────────────────────────
export const GALLERY = {
  headline:    'Life at Grace Pointe',
  subheadline: 'A glimpse into the joy, learning, and community that happens every day.',
  images: [
    { src: '/images/806de4f0-411b-4a06-a5d1-ca971e9ada84.jpg', alt: 'Children enjoying activities at Grace Pointe Child Development Center' },
    { src: '/images/IMG_7365.jpg', alt: 'Grace Pointe children and classroom' },
    { src: '/images/IMG_7370.jpg', alt: 'Children learning together' },
    { src: '/images/IMG_7371.jpg', alt: 'Daycare activities at Grace Pointe' },
    { src: '/images/IMG_7373.jpg', alt: 'Classroom activity at Grace Pointe' },
    { src: '/images/IMG_7384.jpg', alt: 'Children learning and playing' },
    { src: '/images/IMG_7391.jpg', alt: 'Grace Pointe daycare environment' },
    { src: '/images/IMG_7394.jpg', alt: 'Children\'s activities and development' },
    { src: '/images/IMG_7400.jpg', alt: 'Daycare program activities' },
    { src: '/images/IMG_7412.jpg', alt: 'Children at play' },
    { src: '/images/IMG_7413.jpg', alt: 'Learning through play' },
    { src: '/images/IMG_7414.jpg', alt: 'Grace Pointe kids activities' },
    { src: '/images/IMG_7415.jpg', alt: 'Preschool classroom' },
    { src: '/images/IMG_7422.jpg', alt: 'Toddler program activities' },
    { src: '/images/IMG_7423.jpg', alt: 'Infant care program' },
    { src: '/images/IMG_7424.jpg', alt: 'Children learning together' },
    { src: '/images/IMG_7426.jpg', alt: 'Creative play at Grace Pointe' },
    { src: '/images/IMG_7432.jpg', alt: 'Outdoor activities' },
    { src: '/images/IMG_7441.jpg', alt: 'Grace Pointe community' },
    { src: '/images/IMG_7448.jpg', alt: 'Daycare learning experience' },
    { src: '/images/IMG_7455.jpg', alt: 'Children\'s development activities' },
    { src: '/images/IMG_7459.jpg', alt: 'Grace Pointe child care' },
    { src: '/images/IMG_7466.jpg', alt: 'Happy children at Grace Pointe' },
    { src: '/images/IMG_7468.jpg', alt: 'Children exploring and discovering' },
    { src: '/images/IMG_7472.jpg', alt: 'Grace Pointe learning activities' },
    { src: '/images/IMG_7475.jpg', alt: 'Toddler learning time' },
    { src: '/images/IMG_7476.jpg', alt: 'Kids at Grace Pointe CDC' },
    { src: '/images/IMG_7480.jpg', alt: 'Early childhood education activities' },
    { src: '/images/IMG_7497.jpg', alt: 'Grace Pointe preschool program' },
    { src: '/images/IMG_7511.jpg', alt: 'Children at Grace Pointe' },
    { src: '/images/IMG_7537.jpg', alt: 'Daycare fun at Grace Pointe' },
    { src: '/images/IMG_7541.jpg', alt: 'Learning through play' },
    { src: '/images/IMG_7555.jpg', alt: 'Grace Pointe classroom activities' },
    { src: '/images/IMG_7558.jpg', alt: 'Children and teachers at Grace Pointe' },
    { src: '/images/IMG_7559.jpg', alt: 'Creative arts and play' },
    { src: '/images/IMG_7562.jpg', alt: 'Children exploring at Grace Pointe' },
    { src: '/images/IMG_7575.jpg', alt: 'Grace Pointe CDC activities' },
    { src: '/images/IMG_7576.jpg', alt: 'Happy kids at Grace Pointe' },
    { src: '/images/IMG_7579.jpg', alt: 'Children learning and growing' },
    { src: '/images/IMG_7585.jpg', alt: 'Grace Pointe preschoolers' },
    { src: '/images/IMG_7589.jpg', alt: 'Daycare activities and learning' },
    { src: '/images/IMG_7590.jpg', alt: 'Kids enjoying Grace Pointe' },
    { src: '/images/IMG_7613-1.jpg', alt: 'Children at Grace Pointe Child Development Center' },
    { src: '/images/IMG_7614.jpg', alt: 'Learning activities at Grace Pointe' },
    { src: '/images/IMG_7617.jpg', alt: 'Grace Pointe toddler program' },
    { src: '/images/IMG_7625.jpg', alt: 'Children playing and learning' },
    { src: '/images/IMG_7629.jpg', alt: 'Grace Pointe community activities' },
    { src: '/images/IMG_7654.jpg', alt: 'Kids at Grace Pointe CDC' },
    { src: '/images/IMG_7691.jpg', alt: 'Grace Pointe early learning' },
    { src: '/images/IMG_7692.jpg', alt: 'Children engaged in activities' },
    { src: '/images/IMG_7698.jpg', alt: 'Grace Pointe daycare program' },
    { src: '/images/IMG_7699.jpg', alt: 'Happy children at Grace Pointe' },
    { src: '/images/IMG_7701.jpg', alt: 'Grace Pointe child development' },
    { src: '/images/IMG_7702.jpg', alt: 'Children learning at Grace Pointe' },
    { src: '/images/IMG_7707.jpg', alt: 'Daycare activities and community' },
    { src: '/images/IMG_7708-1.jpg', alt: 'Grace Pointe kids having fun' },
    { src: '/images/IMG_7711.jpg', alt: 'Children at play and learning' },
    { src: '/images/IMG_7714.jpg', alt: 'Grace Pointe Child Development Center' },
  ],
}

// ─── About ────────────────────────────────────────────────────────────────────
export const ABOUT = {
  headline: 'About Grace Pointe',
  intro:
    'Grace Pointe Child Development Center was founded on the belief that every child deserves to be seen, known, and loved. Located in the heart of the Avon, Indiana community, we are proud to serve families from across western Indianapolis and the surrounding suburbs.',
  philosophy: {
    headline: 'Our Philosophy',
    body:
      // TODO: Request a full philosophy statement from the owner to personalize this further
      'We believe that the earliest years of life are the most formative — a time when habits of heart and mind are shaped for a lifetime. Our approach blends loving, attentive care with developmentally rich learning experiences grounded in Christian values. We meet each child where they are, celebrating their individuality while building a strong foundation for school and life.',
  },
  whyUs: {
    headline: 'Why Families Choose Grace Pointe',
    points: [
      'A warm, family-like atmosphere where every child is genuinely known and loved',
      'Experienced, caring staff dedicated to each child\'s growth and well-being',
      'A Christ-centered, multicultural environment that welcomes all families',
      'Superior childcare and early education tailored to each developmental stage',
      'Open communication and true partnership with parents',
      'Convenient location serving Avon, Plainfield, and western Indianapolis',
    ],
  },
  teamNote:
    // TODO: Add team bios and photos if the owner is willing to share
    'Our team of dedicated educators brings passion, training, and love to work every single day. We\'d love for you to come meet us on a personal tour.',
}

// ─── Contact ──────────────────────────────────────────────────────────────────
export const CONTACT = {
  headline:    'Get in Touch',
  subheadline:
    'We\'d love to hear from you. Whether you\'re ready to enroll or just have questions, reach out — we\'re here to help.',
  mapPlaceholder:
    // TODO: Add a real Google Maps embed API key or embed URL for the address
    'https://www.google.com/maps/search/?api=1&query=10951+E+CR+100+S+Indianapolis+IN+46231',
}

export const NAV_LINKS = [
  { label: 'Home',       href: '/#home' },
  { label: 'About',      href: '/about' },
  { label: 'Programs',   href: '/programs' },
  { label: 'Gallery',    href: '/gallery' },
  { label: 'Enrollment', href: '/enrollment' },
  { label: 'Contact',    href: '/#contact' },
]
